import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * Badge Component
 * 
 * Display badges for status, counts, labels, and more
 * 
 * @param {string} variant - Badge style: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'
 * @param {string} size - Badge size: 'sm' | 'md' | 'lg'
 * @param {boolean} dot - Show colored dot before text
 * @param {boolean} outline - Use outline style instead of filled
 * @param {ReactNode} children - Badge content
 * @param {string} className - Additional CSS classes
 */
export const Badge = ({
  variant = 'default',
  size = 'md',
  dot = false,
  outline = false,
  className,
  children,
  ...props
}) => {
  // Size styles
  const sizeStyles = {
    sm: cn(
      'h-5 px-2 text-xs',
      dot && 'pl-1.5'
    ),
    md: cn(
      'h-6 px-2.5 text-xs',
      dot && 'pl-2'
    ),
    lg: cn(
      'h-7 px-3 text-sm',
      dot && 'pl-2.5'
    ),
  };

  // Dot sizes
  const dotSizes = {
    sm: 'h-1.5 w-1.5',
    md: 'h-2 w-2',
    lg: 'h-2 w-2',
  };

  // Variant styles - Filled
  const filledVariants = {
    default: 'bg-slate-100 text-slate-700 border-slate-200',
    primary: 'bg-primary-100 text-primary-700 border-primary-200',
    success: 'bg-green-100 text-green-700 border-green-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    error: 'bg-red-100 text-red-700 border-red-200',
    info: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  };

  // Variant styles - Outline
  const outlineVariants = {
    default: 'bg-white text-slate-700 border-slate-300',
    primary: 'bg-white text-primary-700 border-primary-300',
    success: 'bg-white text-green-700 border-green-300',
    warning: 'bg-white text-amber-700 border-amber-300',
    error: 'bg-white text-red-700 border-red-300',
    info: 'bg-white text-cyan-700 border-cyan-300',
  };

  // Dot colors
  const dotColors = {
    default: 'bg-slate-500',
    primary: 'bg-primary-500',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-cyan-500',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5',
        'font-medium rounded-full border',
        'select-none whitespace-nowrap',
        sizeStyles[size],
        outline ? outlineVariants[variant] : filledVariants[variant],
        className
      )}
      {...props}
    >
      {dot && (
        <span className={cn('rounded-full', dotSizes[size], dotColors[variant])} />
      )}
      {children}
    </span>
  );
};

/**
 * CountBadge Component
 * 
 * Display count badge (e.g., notification count)
 * Usually displayed on top-right of an icon or button
 * 
 * @param {number} count - Count to display
 * @param {number} max - Maximum count to display (shows "max+" if exceeded)
 * @param {boolean} dot - Show as dot instead of number (for small counts)
 * @param {string} variant - Badge color variant
 */
export const CountBadge = ({
  count = 0,
  max = 99,
  dot = false,
  variant = 'error',
  className,
  ...props
}) => {
  // Don't show if count is 0
  if (count === 0) return null;

  // Show as dot for count = 1 and dot = true
  if (dot && count === 1) {
    return (
      <span
        className={cn(
          'inline-block h-2 w-2 rounded-full',
          variant === 'error' && 'bg-red-500',
          variant === 'primary' && 'bg-primary-500',
          variant === 'success' && 'bg-green-500',
          variant === 'warning' && 'bg-amber-500',
          className
        )}
        {...props}
      />
    );
  }

  // Display count or "max+"
  const displayCount = count > max ? `${max}+` : count;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'min-w-[20px] h-5 px-1.5',
        'text-[10px] font-semibold leading-none',
        'text-white rounded-full',
        variant === 'error' && 'bg-red-500',
        variant === 'primary' && 'bg-primary-500',
        variant === 'success' && 'bg-green-500',
        variant === 'warning' && 'bg-amber-500',
        className
      )}
      {...props}
    >
      {displayCount}
    </span>
  );
};

/**
 * StatusBadge Component
 * 
 * Predefined status badges for common use cases
 * 
 * @param {string} status - Status type: 'active' | 'pending' | 'completed' | 'cancelled' | 'draft'
 */
export const StatusBadge = ({ status, ...props }) => {
  const statusConfig = {
    active: { variant: 'success', text: 'Active', dot: true },
    pending: { variant: 'warning', text: 'Pending', dot: true },
    completed: { variant: 'primary', text: 'Completed', dot: false },
    cancelled: { variant: 'error', text: 'Cancelled', dot: false },
    draft: { variant: 'default', text: 'Draft', dot: false },
    closed: { variant: 'default', text: 'Closed', dot: false },
    open: { variant: 'success', text: 'Open', dot: true },
    in_progress: { variant: 'primary', text: 'In Progress', dot: true },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <Badge
      variant={config.variant}
      dot={config.dot}
      size="sm"
      {...props}
    >
      {config.text}
    </Badge>
  );
};

export default Badge;